import { Component, OnInit, OnDestroy } from '@angular/core';
import { NotificationService } from '../../shared/services';
import { Subscription, throttleTime } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { Service, Terminal, TerminalPayload, Ticket } from '../../shared/models';
import { TerminalService } from '../../shared/services/terminal.service';
import { StorageService } from '../../shared/services';
import { TicketService } from '../../shared/services/ticket.service';
import { MatDialog } from '@angular/material/dialog';
import { brighten } from '../../shared/utils/colors';
import { TermsModalComponent } from '../../components/terms-modal/terms-modal.component';
import { TermsNoelModalComponent } from '../../components/terms-noel-modal/terms-noel-modal.component';
import { environment } from 'src/environments/environment';
import { PrivacyPolicyModalComponent } from '../../components/privacy-policy-modal/privacy-policy-modal.component';
import { ResumeTicketItem } from '../../components/resume-ticket-page/resume-ticket-page.component';
import * as moment from 'moment-timezone';

// Assaí Atacadista: ao ler o QR code, vai direto para o bilhete mais recente sem exibir a tela de escolha.
// 1362 = produção | 3367 = homologação/dev
const AUTO_RESUME_RECENT_TICKET_PROVIDER_IDS = [1362, 3367];

@Component({
  selector: 'app-start',
  templateUrl: './start.component.html',
  styleUrls: ['./start.component.scss']
})
export class StartComponent implements OnInit, OnDestroy {
  isChecked = false;
  terminal: Terminal;
  payload: TerminalPayload;
  isLoading = true;
  container: HTMLElement;
  accessKey: string;
  resumeTickets: ResumeTicketItem[] | null = null;
  private subscription?: Subscription;
  private connectionSubscription?: Subscription;
  /**
   * Guarda contra varreduras concorrentes. O throttle emite 1/s + trailing,
   * mas `resumeActiveTicket` (N GETs sequenciais) pode durar mais que a janela:
   * sem a guarda, o trailing disparava com a anterior em voo e as duas
   * escreviam `resumeTickets`/storage com interleave.
   */
  private resuming = false;
  private resumeQueued = false;
  /**
   * Janela de coalescência do rebuild da lista. `resumeActiveTicket` refaz um
   * GET por bilhete local, então uma rajada de eventos disparava uma rajada de
   * varreduras idênticas.
   */
  private static readonly RESUME_THROTTLE_MS = 1000;
  private notificationEvents = [
    'TicketCheckin', 'TicketConfirm', 'PrevisionChanged', 'TicketCalled',
    'TicketStart', 'TicketComplete', 'TicketCancel', 'TicketReopened',
    'TransferredTicket', 'ServiceConfigTicketCalled'
  ];

  constructor(
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private storageService: StorageService,
    private terminalService: TerminalService,
    private ticketService: TicketService,
    private router: Router,
    private notificationService: NotificationService
  ) { }

  ngOnInit() {
    this.payload = {} as TerminalPayload;
    this.route.paramMap
      .subscribe(params => {
        const accessKey = params.get('accessKey');
        const startNewQueue = this.route.snapshot.queryParamMap.get('newQueue') === 'true';
        this.accessKey = accessKey;

        localStorage.setItem('terminalKey', accessKey);

        if (accessKey) {
          this.terminalService.getTerminal(accessKey)
            .subscribe(async terminal => {
              if (!(terminal.messages && terminal.messages[0].type === 'ERROR')) {
                this.terminal = terminal;
                this.storageService.addTerminal(terminal, accessKey);
                this.storageService.setTemplate({templateColor: this.terminal.headerBackgroundColor, logoUrl: this.terminal.providerLogoUrl })
                this.payload = this.terminalService.createPayload(terminal.provider.id, terminal.location.id, accessKey, terminal.id);

                if (!startNewQueue && await this.resumeActiveTicket(terminal, accessKey)) {
                  return;
                }
              }

              this.showStart();
            }, () => this.showStart());
        }
      });
  }

  private async resumeActiveTicket(terminal: Terminal, accessKey = this.accessKey): Promise<boolean> {
    const activeStatuses = ['Opened', 'Authorized', 'Reopened', 'Confirmed', 'Checked', 'Called', 'Started'];
    const tickets = [...this.storageService.tickets].reverse().filter(ticket =>
      ticket.providerId === terminal.provider.id && ticket.locationId === terminal.location.id
    );
    const activeTickets: Array<ResumeTicketItem & { service: Service; lastTouchedAt: number | null; order: number }> = [];

    for (const [order, ticket] of tickets.entries()) {
      let response: any;
      try {
        response = await this.ticketService.getTicket(ticket.id).toPromise();
      } catch (error) {
        if (error?.status === 404) {
          continue;
        }
        // A falha de rede não prova que o bilhete terminou. Mantemos o histórico local.
        return false;
      }

      if (response?.provider?.id !== terminal.provider.id ||
          response?.location?.id !== terminal.location.id ||
          !response?.session?.serviceConfigId ||
          !response?.service?.id ||
          !response?.accessKey ||
          !activeStatuses.includes(response.status) ||
          this.isTicketExpired(ticket, response, terminal)) {
        continue;
      }

      const resolvedQueue = await this.resolveTicketQueue(ticket, response, terminal, accessKey);

      const currentTicket: Ticket = {
        ...ticket,
        terminalAccessKey: resolvedQueue.queueAccessKey || ticket.terminalAccessKey,
        status: response.status,
        accessKey: response.accessKey,
        friendlyCode: response.friendlyCode || ticket.friendlyCode,
        serviceConfigId: response.session.serviceConfigId,
        position: response.position ?? ticket.position,
        prevision: response.estimatedServiceStart ?? response.session?.estimatedStart ?? ticket.prevision
      };

      this.storageService.updateTicket(currentTicket);
      activeTickets.push({
        ticket: currentTicket,
        serviceName: response.service?.name || resolvedQueue.service?.name || ticket.serviceName,
        queueName: response.session.serviceConfigName,
        customerName: response.customer?.name,
        service: resolvedQueue.service,
        queueAccessKey: resolvedQueue.queueAccessKey,
        canContinue: resolvedQueue.canContinue,
        lastTouchedAt: this.getLastTouchedAt(ticket, response),
        order
      });
    }

    if (!activeTickets.length) {
      return false;
    }

    activeTickets.sort((first, second) => {
      if (first.lastTouchedAt !== null && second.lastTouchedAt !== null) {
        return second.lastTouchedAt - first.lastTouchedAt;
      }
      if (first.lastTouchedAt !== null) return -1;
      if (second.lastTouchedAt !== null) return 1;
      return first.order - second.order;
    });

    if (AUTO_RESUME_RECENT_TICKET_PROVIDER_IDS.includes(terminal.provider.id)) {
      const recentTicket = activeTickets.find(item => item.canContinue);
      if (recentTicket) {
        // replaceUrl evita que o voltar do navegador caia no /start e retome de novo em loop.
        // Se a navegação falhar, cai na tela inicial em vez de ficar preso no spinner.
        this.continueTicket(recentTicket, true)
          .then(navigated => { if (!navigated) this.showStart(); })
          .catch(() => this.showStart());
        return true;
      }
    }

    this.isLoading = false;
    this.resumeTickets = activeTickets;
    this.registerResumeNotifications(activeTickets);
    return true;
  }

  private registerResumeNotifications(tickets: ResumeTicketItem[]): void {
    if (!this.terminal) return;
    for (const item of tickets) {
      this.notificationService.add(item.ticket.id, item.ticket.serviceConfigId, this.notificationEvents);
    }
    if (!this.subscription) {
      // Coalesce rajadas: qualquer evento ainda atualiza a lista (leading + trailing),
      // mas no máximo uma varredura pesada por janela. Não filtra por id de propósito:
      // um bilhete pode ENTRAR na lista por um evento de outro bilhete da mesma fila.
      this.subscription = this.notificationService.notifications
        .pipe(throttleTime(StartComponent.RESUME_THROTTLE_MS, undefined, { leading: true, trailing: true }))
        .subscribe(() => this.runResume());

      // Eventos perdidos durante uma queda de conexão não são reentregues: a lista
      // pode estar mostrando um bilhete que já terminou. Revalida ao voltar.
      this.connectionSubscription = this.notificationService.reconnections
        .subscribe(() => this.runResume());
    }
  }

  /**
   * Serializa as varreduras: se uma estiver em voo, marca rerun em vez de
   * concorrer. Garante que nenhuma atualização se perde (ao contrário de
   * `exhaustMap`, que descartaria o trailing).
   */
  private async runResume(): Promise<void> {
    if (this.resuming) {
      this.resumeQueued = true;
      return;
    }
    this.resuming = true;
    try {
      do {
        this.resumeQueued = false;
        await this.resumeActiveTicket(this.terminal!, this.accessKey);
      } while (this.resumeQueued);
    } finally {
      this.resuming = false;
    }
  }

  private async resolveTicketQueue(
    ticket: Ticket,
    response: any,
    currentTerminal: Terminal,
    currentAccessKey: string
  ): Promise<{ queueAccessKey?: string; service: Service; canContinue: boolean }> {
    const serviceId = response.service?.id;
    const currentUnitTerminal = { ...currentTerminal, accessKey: currentAccessKey };
    const savedTerminals = this.storageService.terminals || [];
    const unitTerminals = [currentUnitTerminal, ...savedTerminals].filter((item, index, all) =>
      item.provider?.id === currentTerminal.provider.id &&
      item.location?.id === currentTerminal.location.id &&
      all.findIndex(other => other.id === item.id) === index
    );
    const serviceTerminals = unitTerminals.filter(item => item.services?.some(service => service.id === serviceId));

    if (ticket.terminalAccessKey) {
      const cachedOrigin = savedTerminals.find(item => item.accessKey === ticket.terminalAccessKey);
      let originOutsideUnit = !!cachedOrigin && (
        cachedOrigin.provider?.id !== currentTerminal.provider.id ||
        cachedOrigin.location?.id !== currentTerminal.location.id
      );
      let originTerminal = unitTerminals.find(item => item.accessKey === ticket.terminalAccessKey);
      if (!originTerminal && ticket.terminalAccessKey === currentAccessKey) {
        originTerminal = currentTerminal;
      }

      if (!originTerminal && ticket.terminalAccessKey !== currentAccessKey) {
        try {
          originTerminal = await this.terminalService.getTerminal(ticket.terminalAccessKey).toPromise();
          if (originTerminal?.provider?.id === currentTerminal.provider.id &&
              originTerminal?.location?.id === currentTerminal.location.id) {
            this.storageService.addTerminal(originTerminal, ticket.terminalAccessKey, false);
          } else {
            originOutsideUnit = true;
            originTerminal = undefined;
          }
        } catch {
          originTerminal = undefined;
        }
      }

      const originService = originTerminal?.services?.find(service => service.id === serviceId);
      if (originService) {
        return {
          queueAccessKey: ticket.terminalAccessKey,
          service: originService,
          canContinue: true
        };
      }

      if (!originTerminal && !originOutsideUnit) {
        return {
          queueAccessKey: ticket.terminalAccessKey,
          service: serviceTerminals[0]?.services.find(service => service.id === serviceId) || this.createTicketService(response),
          canContinue: true
        };
      }
    }

    if (serviceTerminals.length === 1 && serviceTerminals[0].accessKey) {
      return {
        queueAccessKey: serviceTerminals[0].accessKey,
        service: serviceTerminals[0].services.find(service => service.id === serviceId),
        canContinue: true
      };
    }

    return {
      service: serviceTerminals[0]?.services.find(service => service.id === serviceId) || this.createTicketService(response),
      canContinue: false
    };
  }

  private createTicketService(response: any): Service {
    return {
      id: response.service?.id,
      name: response.service?.name,
      sessions: []
    } as Service;
  }

  continueTicket(item: ResumeTicketItem, replaceUrl = false): Promise<boolean> {
    this.storageService.setCurrentService(item.service);
    this.storageService.updateTicket(item.ticket);
    const commands = ['/queue', item.queueAccessKey || this.accessKey, 'ticket', item.ticket.accessKey];
    if (replaceUrl) {
      return this.router.navigate(commands, { replaceUrl: true });
    }
    return this.router.navigate(commands);
  }

  createNewTicket(): void {
    this.resumeTickets = null;
    this.showStart();
  }

  private getLastTouchedAt(ticket: Ticket, response: any): number | null {
    const timestamps = [response?.updatedAt, response?.lastUpdate]
      .map(value => value ? new Date(value).getTime() : NaN)
      .filter(value => Number.isFinite(value));
    const activities = [...(ticket?.activities || []), ...(response?.activities || [])];

    activities.forEach(activity => {
      const timestamp = activity?.at ? new Date(activity.at).getTime() : NaN;
      if (Number.isFinite(timestamp)) timestamps.push(timestamp);
    });

    return timestamps.length ? Math.max(...timestamps) : null;
  }

  private isTicketExpired(ticket: Ticket, response: any, terminal: Terminal): boolean {
    const timezoneId = [response.location?.timezoneId, ticket.timezoneId, terminal.timezoneId]
      .find(value => value && moment.tz.zone(value));
    const now = timezoneId ? moment().tz(timezoneId) : moment();
    const today = now.startOf('day');
    const sessionEnd = moment(response.session?.estimatedEnd || null, moment.ISO_8601, true);

    if (sessionEnd.isValid() && sessionEnd.isBefore(today)) return true;

    const attendanceDate = [
      response.estimatedServiceStart,
      response.session?.estimatedStart,
      response.session?.estimatedEnd,
      ticket.prevision
    ]
      .map(value => moment(value || null, moment.ISO_8601, true))
      .find(value => value.isValid());

    // Sem uma data válida, não é possível oferecer um bilhete antigo como atendimento em andamento.
    return !attendanceDate || attendanceDate.isBefore(today);
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.connectionSubscription?.unsubscribe();
  }

  private showStart(): void {
    this.isLoading = false;
    if (!this.terminal) {
      return;
    }
    const interval = setInterval(() => {
      this.container = document.getElementById('welcome-wrapper');
      if (this.container) {
        this.container.style.height = `calc(${window.innerHeight}px - (64px + 40px))`;
        clearInterval(interval);
      }
    }, 250);
  }

  get isValid() { return this.isChecked; }

  next(): void {
    this.router.navigate(['/queue', this.accessKey, 'schedule']);
  }

  brighten(color: string, amount: number): string {
    return '#' + brighten(color, amount);
  }

  get isPhoneNoel() {
    return this.terminal.provider.id === environment.phoneNoelProviderId;
  }

  openTermsNoelModal() {
    this.dialog.open(TermsNoelModalComponent, {
      minWidth: '100vw',
      height: '100vh'
    });
  }

  openTermsModal() {
    this.dialog.open(TermsModalComponent, {
      minWidth: '100vw',
      height: '100vh'
    });
  }

  openPrivacyPolicyModal() {
    this.dialog.open(PrivacyPolicyModalComponent, {
      minWidth: '100vw',
      height: '100vh'
    })
  }
}
